import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { ProtectedFunctionRoute } from './components/auth/ProtectedFunctionRoute';
import { FEE_CATEGORY_PERMISSIONS, APARTMENT_PERMISSIONS, ACCOUNT_PERMISSIONS, BUILDING_BANNER_PERMISSIONS, BUILDING_NEWS_PERMISSIONS, BUILDING_UTILITY_PERMISSIONS, BUILDING_UTILITY_GROUP_PERMISSIONS, BUILDING_UTILITY_BOOKING_PERMISSIONS, CONSTRUCTION_REGISTRATION_PERMISSIONS, ENTRY_EXIT_REGISTRATION_PERMISSIONS, MOVING_REGISTRATION_PERMISSIONS, TASK_PERMISSIONS } from './constants/functionPermissions';
import { Login } from './pages/Login';
import { ForgotPassword } from './pages/ForgotPassword';
import { ResetPassword } from './pages/ResetPassword';
import { MyAccount } from './pages/MyAccount';
import { PermissionGroupAdd } from './pages/PermissionGroupAdd';
import { PermissionGroupList } from './pages/PermissionGroupList';
import { PermissionGroupEdit } from './pages/PermissionGroupEdit';
import { AddAccount } from './pages/AddAccount';
import { AccountList } from './pages/AccountList';
import { EditAccount } from './pages/EditAccount';
import { FunctionList } from './pages/FunctionList';
import { AddFunction } from './pages/AddFunction';
import { EditFunction } from './pages/EditFunction';
import { FunctionCategories } from './pages/FunctionCategories';
import { AddBuilding } from './pages/buildings/AddBuilding';
import { BuildingList } from './pages/buildings/BuildingList';
import { EditBuilding } from './pages/buildings/EditBuilding';
import FeeCategoryList from './pages/FeeCategories/FeeCategoryList';
import AddFeeCategory from './pages/FeeCategories/AddFeeCategory';
import EditFeeCategory from './pages/FeeCategories/EditFeeCategory';
import BulkFeeEntry from './pages/BulkFeeEntry';
import ApartmentList from './pages/apartments/ApartmentList';
import AddApartment from './pages/apartments/AddApartment';
import EditApartment from './pages/apartments/EditApartment';
import ApartmentDetailsEnhanced from './pages/apartments/ApartmentDetailsEnhanced';
import SearchApartments from './pages/apartments/SearchApartments';
import ApartmentGroups from './pages/apartments/ApartmentGroups';
import ApartmentUsageTypes from './pages/apartments/ApartmentUsageTypes';
import ResidentList from './pages/apartments/ResidentList';
import MonthlyVehicleManagement from './pages/apartments/MonthlyVehicleManagement';
import CardManagement from './pages/apartments/CardManagement';
import { EmailNotifications } from './pages/EmailNotifications';
import { SendEmail } from './pages/SendEmail';
import { EmailList } from './pages/EmailList';
import { AutomationSettings } from './pages/AutomationSettings';
import { Support } from './pages/Support';
import Settings from './pages/Settings';
import { SendFeeNotification } from './pages/SendFeeNotification';
import ExportPdfServiceFees from './pages/ExportPdfServiceFees';
import FeeNoticeBatchList from './pages/FeeNoticeBatchList';
import PdfTemplates from './pages/PdfTemplates';
import { Payment } from './pages/Payment';
import { TransactionReport } from './pages/TransactionReport';
import { ReceivableReport } from './pages/ReceivableReport';
import ServiceFeeManagement from './pages/ServiceFeeManagement/ServiceFeeManagement';
import ContractListPage from './pages/contracts/ContractListPage';
import AddEditContractPage from './pages/contracts/AddEditContractPage';
import CreateExpenditureVoucherPage from './pages/contracts/CreateExpenditureVoucherPage';
import RevenueActivitiesPage from './pages/income-expense/RevenueActivitiesPage';
import ExpenditureActivitiesPage from './pages/income-expense/ExpenditureActivitiesPage';
import IncomeExpenseSummaryPage from './pages/income-expense/IncomeExpenseSummaryPage';
import BankAccountsPage from './pages/income-expense/BankAccountsPage';
import FinancialReportsPage from './pages/income-expense/FinancialReportsPage';
import DataImportPage from './pages/income-expense/DataImportPage';
import { LangProvider, useLang } from './contexts/LangContext';
import { Sidebar } from './components/common/Sidebar';
import AddTask from './pages/tasks/AddTask';
import { TaskList } from './pages/tasks/TaskList';
import TaskDetails from './pages/tasks/TaskDetails';
import { BuildingUtilityGroups, BuildingUtilityList, BuildingUtilityBookings } from './pages/building-utilities';
import { AddEditUtilityGroup } from './pages/building-utilities/AddEditUtilityGroup';
import { AddEditUtility } from './pages/building-utilities/AddEditUtility';
import { AddEditBooking } from './pages/building-utilities/AddEditBooking';
import ConstructionRegistrationList from './pages/building-services/ConstructionRegistrationList';
import AddEditConstructionRegistration from './pages/building-services/AddEditConstructionRegistration';
import EntryExitRegistrationList from './pages/building-services/EntryExitRegistrationList';
import MovingRegistrationList from './pages/building-services/MovingRegistrationList';
import AddEditEntryExitRegistration from './pages/building-services/AddEditEntryExitRegistration';
import AddEditMovingRegistration from './pages/building-services/AddEditMovingRegistration';
import { TaskStatus } from './types';
import FeedbackList from './pages/feedbacks/FeedbackList';
import FeedbackCategoryConfig from './pages/feedbacks/FeedbackCategoryConfig';
import FeedbackStatistics from './pages/feedbacks/FeedbackStatistics';
import FeedbackDetails from './pages/feedbacks/FeedbackDetails';
import ChatPage from './pages/ChatPage';
import { DynamicSidebar } from './components/common/DynamicSidebar';
import { useAuth } from './contexts/AuthContext';
import BillInvoice from './pages/BillInvoice';
import Dashboard from './pages/Dashboard';
import { BuildingBannerList, AddEditBanner, BuildingNewsList, AddEditNews } from './pages/building-home-content';
import { AppBrandingHead } from './hooks/useAppBranding';
import { NotificationsProvider } from './contexts/NotificationsContext';
import { NotificationBell } from './components/notifications/NotificationBell';
import NotificationsPage from './pages/notifications/NotificationsPage';

const LangToggle: React.FC<{ className?: string }> = ({ className = '' }) => {
  const { lang, setLang } = useLang();

  const handleLanguageChange = (newLang: 'vi' | 'en') => {
    setLang(newLang);
    localStorage.setItem('lang', newLang);
  };

  return (
    <div className={`inline-flex rounded-md overflow-hidden border border-gray-200 flex-shrink-0 ${className}`}>
      <button
        type="button"
        className={`px-2.5 py-1 text-xs font-medium ${lang === 'vi' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
        onClick={() => handleLanguageChange('vi')}
      >
        VI
      </button>
      <button
        type="button"
        className={`px-2.5 py-1 text-xs font-medium ${lang === 'en' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
        onClick={() => handleLanguageChange('en')}
      >
        EN
      </button>
    </div>
  );
};

const AppLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(true);
  const { user } = useAuth();

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  return (
  <div className="flex min-h-screen bg-gray-50">
      <DynamicSidebar isOpen={isSidebarOpen} onClose={closeSidebar} />
    <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ease-in-out ${isSidebarOpen ? 'lg:ml-80' : 'lg:ml-0'}`}>
      <header className="bg-white shadow-sm border-b border-gray-200 px-4 sm:px-6 py-3">
          <div className="flex items-center gap-3 min-w-0">
            {/* Mobile menu button */}
            <button
              onClick={toggleSidebar}
              className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 flex-shrink-0"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            
            {/* Desktop menu button */}
            <button
              onClick={toggleSidebar}
              className="hidden lg:block p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 flex-shrink-0"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>

            <div className="flex items-center gap-2 sm:gap-3 min-w-0 ml-auto">
              {user && <NotificationBell />}
              {user && (
                <Link
                  to="/my-account"
                  title={`${user.name}${user.username ? ` (${user.username})` : ''}`}
                  className="flex items-center gap-2 sm:gap-3 min-w-0 max-w-[10rem] sm:max-w-[14rem] px-2 sm:px-3 py-1.5 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="w-8 h-8 sm:w-9 sm:h-9 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                    {(user.name || user.username || user.email || '?').charAt(0).toUpperCase()}
                  </div>
                  <div className="hidden sm:block min-w-0 overflow-hidden text-left">
                    <div className="text-sm font-medium text-gray-900 truncate">{user.name}</div>
                    <div className="text-xs text-gray-500 truncate">{user.username || user.email}</div>
                  </div>
                </Link>
              )}
              <LangToggle />
            </div>
        </div>
      </header>
      <main className="flex-1 p-6 overflow-auto">
        <div className="max-w-full">
          {children}
        </div>
      </main>
    </div>
  </div>
);
};

export const App: React.FC = () => {
  return (
    <LangProvider>
      <Router>
        <AuthProvider>
          <NotificationsProvider>
          <AppBrandingHead />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            {/* Public printable invoice page (openable from mobile via URL) */}
            <Route path="/bills/:billId/invoice" element={<BillInvoice />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Dashboard />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-account"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <MyAccount />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/notifications"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <NotificationsPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            {/* Account Management Routes */}
            <Route
              path="/accounts"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <AccountList />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/accounts/add"
              element={
                <ProtectedRoute>
                  <ProtectedFunctionRoute codes={ACCOUNT_PERMISSIONS} require="create" redirectTo="/accounts">
                    <AppLayout>
                      <AddAccount />
                    </AppLayout>
                  </ProtectedFunctionRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/accounts/:id/edit"
              element={
                <ProtectedRoute>
                  <ProtectedFunctionRoute codes={ACCOUNT_PERMISSIONS} require="edit" redirectTo="/accounts">
                    <AppLayout>
                      <EditAccount />
                    </AppLayout>
                  </ProtectedFunctionRoute>
                </ProtectedRoute>
              }
            />
            {/* Permission Group Routes */}
            <Route
              path="/permission-groups"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <PermissionGroupList />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/permission-groups/add"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <PermissionGroupAdd />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/permission-groups/:id/edit"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <PermissionGroupEdit />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            {/* Function Management Routes */}
            <Route
              path="/functions"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <FunctionList />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/functions/add"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <AddFunction />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/functions/:id/edit"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <EditFunction />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            {/* Function Categories Routes */}
            <Route
              path="/function-categories"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <FunctionCategories />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            {/* Building Management Routes */}
            <Route
              path="/buildings"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <BuildingList />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/buildings/add"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <AddBuilding />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/buildings/:id/edit"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <EditBuilding />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/send-fee-notification"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <SendFeeNotification />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/export-pdf-service-fees"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <ExportPdfServiceFees />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/pdf-exports/:exportId/notices"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <FeeNoticeBatchList />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/pdf-templates"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <PdfTemplates />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            {/* Fee Category Routes */}
            <Route
              path="/fee-categories"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <FeeCategoryList />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/fee-categories/add"
              element={
                <ProtectedRoute>
                  <ProtectedFunctionRoute codes={FEE_CATEGORY_PERMISSIONS} require="create" redirectTo="/fee-categories">
                    <AppLayout>
                      <AddFeeCategory />
                    </AppLayout>
                  </ProtectedFunctionRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/fee-categories/:id/edit"
              element={
                <ProtectedRoute>
                  <ProtectedFunctionRoute codes={FEE_CATEGORY_PERMISSIONS} require="edit" redirectTo="/fee-categories">
                    <AppLayout>
                      <EditFeeCategory />
                    </AppLayout>
                  </ProtectedFunctionRoute>
                </ProtectedRoute>
              }
            />
            {/* Bulk Fee Entry Route */}
            <Route
              path="/bulk-fee-entry"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <BulkFeeEntry />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            {/* Utilities Routes */}
            <Route
              path="/send-email"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <SendEmail />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/email-list"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <EmailList />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/email-notifications"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <EmailNotifications />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/automation-settings"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <AutomationSettings />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/support"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Support />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Settings />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            {/* Apartment Management Routes */}
            <Route
              path="/apartments"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <ApartmentList />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/apartments/add"
              element={
                <ProtectedRoute>
                  <ProtectedFunctionRoute codes={APARTMENT_PERMISSIONS} require="create" redirectTo="/apartments">
                    <AppLayout>
                      <AddApartment />
                    </AppLayout>
                  </ProtectedFunctionRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/apartments/:id/edit"
              element={
                <ProtectedRoute>
                  <ProtectedFunctionRoute codes={APARTMENT_PERMISSIONS} require="edit" redirectTo="/apartments">
                    <AppLayout>
                      <EditApartment />
                    </AppLayout>
                  </ProtectedFunctionRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/apartments/:id/details"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <ApartmentDetailsEnhanced />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/apartments/groups"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <ApartmentGroups />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/apartments/usage-types"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <ApartmentUsageTypes />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/apartments/residents"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <ResidentList />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/apartments/monthly-vehicles"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <MonthlyVehicleManagement />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/apartments/card-management"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <CardManagement />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/apartments/search"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <SearchApartments />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            {/* Service Fee Routes */}
            <Route
              path="/payment"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Payment />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/transaction-report"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <TransactionReport />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/receivable-report"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <ReceivableReport />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/service-fee-management"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <ServiceFeeManagement />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/contracts/revenue"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <ContractListPage contractType="REVENUE" />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/contracts/revenue/add"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <AddEditContractPage contractType="REVENUE" />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/contracts/revenue/:id/edit"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <AddEditContractPage contractType="REVENUE" />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/contracts/expenditure"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <ContractListPage contractType="EXPENDITURE" />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/contracts/expenditure/add"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <AddEditContractPage contractType="EXPENDITURE" />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/contracts/expenditure/:id/edit"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <AddEditContractPage contractType="EXPENDITURE" />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/contracts/expenditure/:id/expenditure-voucher"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <CreateExpenditureVoucherPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            {/* Income & Expenditure Management Routes */}
            <Route
              path="/income-expense/revenue-activities"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <RevenueActivitiesPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/income-expense/expenditure-activities"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <ExpenditureActivitiesPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/income-expense/summary"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <IncomeExpenseSummaryPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/income-expense/bank-accounts"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <BankAccountsPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/income-expense/financial-reports"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <FinancialReportsPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/income-expense/import"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <DataImportPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            {/* Building Utilities Routes */}
            <Route
              path="/building-utility-groups"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <BuildingUtilityGroups />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/building-utility-groups/add"
              element={
                <ProtectedRoute>
                  <ProtectedFunctionRoute codes={BUILDING_UTILITY_GROUP_PERMISSIONS} require="create" redirectTo="/building-utility-groups">
                    <AppLayout>
                      <AddEditUtilityGroup />
                    </AppLayout>
                  </ProtectedFunctionRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/building-utility-groups/:id/edit"
              element={
                <ProtectedRoute>
                  <ProtectedFunctionRoute codes={BUILDING_UTILITY_GROUP_PERMISSIONS} require="edit" redirectTo="/building-utility-groups">
                    <AppLayout>
                      <AddEditUtilityGroup />
                    </AppLayout>
                  </ProtectedFunctionRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/building-utilities"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <BuildingUtilityList />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/building-utilities/add"
              element={
                <ProtectedRoute>
                  <ProtectedFunctionRoute codes={BUILDING_UTILITY_PERMISSIONS} require="create" redirectTo="/building-utilities">
                    <AppLayout>
                      <AddEditUtility />
                    </AppLayout>
                  </ProtectedFunctionRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/building-utilities/:id/edit"
              element={
                <ProtectedRoute>
                  <ProtectedFunctionRoute codes={BUILDING_UTILITY_PERMISSIONS} require="edit" redirectTo="/building-utilities">
                    <AppLayout>
                      <AddEditUtility />
                    </AppLayout>
                  </ProtectedFunctionRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/building-utility-bookings"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <BuildingUtilityBookings />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/building-utility-bookings/add"
              element={
                <ProtectedRoute>
                  <ProtectedFunctionRoute codes={BUILDING_UTILITY_BOOKING_PERMISSIONS} require="create" redirectTo="/building-utility-bookings">
                    <AppLayout>
                      <AddEditBooking />
                    </AppLayout>
                  </ProtectedFunctionRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/building-utility-bookings/:id/edit"
              element={
                <ProtectedRoute>
                  <ProtectedFunctionRoute codes={BUILDING_UTILITY_BOOKING_PERMISSIONS} require="edit" redirectTo="/building-utility-bookings">
                    <AppLayout>
                      <AddEditBooking />
                    </AppLayout>
                  </ProtectedFunctionRoute>
                </ProtectedRoute>
              }
            />
            {/* Building Home Content Routes */}
            <Route
              path="/building-banners"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <BuildingBannerList />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/building-banners/add"
              element={
                <ProtectedRoute>
                  <ProtectedFunctionRoute codes={BUILDING_BANNER_PERMISSIONS} require="create" redirectTo="/building-banners">
                    <AppLayout>
                      <AddEditBanner />
                    </AppLayout>
                  </ProtectedFunctionRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/building-banners/:id/edit"
              element={
                <ProtectedRoute>
                  <ProtectedFunctionRoute codes={BUILDING_BANNER_PERMISSIONS} require="edit" redirectTo="/building-banners">
                    <AppLayout>
                      <AddEditBanner />
                    </AppLayout>
                  </ProtectedFunctionRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/building-news"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <BuildingNewsList />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/building-news/add"
              element={
                <ProtectedRoute>
                  <ProtectedFunctionRoute codes={BUILDING_NEWS_PERMISSIONS} require="create" redirectTo="/building-news">
                    <AppLayout>
                      <AddEditNews />
                    </AppLayout>
                  </ProtectedFunctionRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/building-news/:id/edit"
              element={
                <ProtectedRoute>
                  <ProtectedFunctionRoute codes={BUILDING_NEWS_PERMISSIONS} require="edit" redirectTo="/building-news">
                    <AppLayout>
                      <AddEditNews />
                    </AppLayout>
                  </ProtectedFunctionRoute>
                </ProtectedRoute>
              }
            />
            {/* Building Services Routes */}
            <Route
              path="/building-services/construction"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <ConstructionRegistrationList />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/building-services/construction/add"
              element={
                <ProtectedRoute>
                  <ProtectedFunctionRoute codes={CONSTRUCTION_REGISTRATION_PERMISSIONS} require="create" redirectTo="/building-services/construction">
                    <AppLayout>
                      <AddEditConstructionRegistration />
                    </AppLayout>
                  </ProtectedFunctionRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/building-services/construction/:id/edit"
              element={
                <ProtectedRoute>
                  <ProtectedFunctionRoute codes={CONSTRUCTION_REGISTRATION_PERMISSIONS} require="edit" redirectTo="/building-services/construction">
                    <AppLayout>
                      <AddEditConstructionRegistration />
                    </AppLayout>
                  </ProtectedFunctionRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/building-services/entry-exit"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <EntryExitRegistrationList />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/building-services/entry-exit/add"
              element={
                <ProtectedRoute>
                  <ProtectedFunctionRoute codes={ENTRY_EXIT_REGISTRATION_PERMISSIONS} require="create" redirectTo="/building-services/entry-exit">
                    <AppLayout>
                      <AddEditEntryExitRegistration />
                    </AppLayout>
                  </ProtectedFunctionRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/building-services/entry-exit/:id/edit"
              element={
                <ProtectedRoute>
                  <ProtectedFunctionRoute codes={ENTRY_EXIT_REGISTRATION_PERMISSIONS} require="edit" redirectTo="/building-services/entry-exit">
                    <AppLayout>
                      <AddEditEntryExitRegistration />
                    </AppLayout>
                  </ProtectedFunctionRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/building-services/moving"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <MovingRegistrationList />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/building-services/moving/add"
              element={
                <ProtectedRoute>
                  <ProtectedFunctionRoute codes={MOVING_REGISTRATION_PERMISSIONS} require="create" redirectTo="/building-services/moving">
                    <AppLayout>
                      <AddEditMovingRegistration />
                    </AppLayout>
                  </ProtectedFunctionRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/building-services/moving/:id/edit"
              element={
                <ProtectedRoute>
                  <ProtectedFunctionRoute codes={MOVING_REGISTRATION_PERMISSIONS} require="edit" redirectTo="/building-services/moving">
                    <AppLayout>
                      <AddEditMovingRegistration />
                    </AppLayout>
                  </ProtectedFunctionRoute>
                </ProtectedRoute>
              }
            />
            {/* Task Management Routes */}
            <Route
              path="/tasks/add"
              element={
                <ProtectedRoute>
                  <ProtectedFunctionRoute codes={TASK_PERMISSIONS} require="create" redirectTo="/tasks">
                    <AppLayout>
                      <AddTask />
                    </AppLayout>
                  </ProtectedFunctionRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/tasks"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <TaskList />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/tasks/incomplete"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <TaskList status={TaskStatus.PENDING} />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/tasks/completed"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <TaskList status={TaskStatus.COMPLETED} />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/tasks/:id/details"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <TaskDetails />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            {/* Chat Routes */}
            <Route
              path="/chat"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <ChatPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/chat/:roomId"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <ChatPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            {/* Feedback Management Routes */}
            <Route
              path="/feedbacks"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <FeedbackList />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/feedbacks/categories"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <FeedbackCategoryConfig />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/feedbacks/statistics"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <FeedbackStatistics />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/feedbacks/:id"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <FeedbackDetails />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
          </Routes>
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#363636',
                color: '#fff',
                borderRadius: '8px',
                padding: '12px 16px',
                fontSize: '14px',
                maxWidth: '500px',
              },
              success: {
                style: {
                  background: '#059669',
                },
                iconTheme: {
                  primary: '#fff',
                  secondary: '#059669',
                },
              },
              error: {
                style: {
                  background: '#DC2626',
                },
                iconTheme: {
                  primary: '#fff',
                  secondary: '#DC2626',
                },
              },
              loading: {
                style: {
                  background: '#3B82F6',
                },
              },
            }}
          />
          </NotificationsProvider>
        </AuthProvider>
      </Router>
    </LangProvider>
  );
}; 
